import { V1AssetResponse, V1AssetsResponse, V1ResolveAmbiguousAssetsResponse, V1ResolveAssetsResponse } from '@blk/portfolio-security-server/asset';
import { PasErrorCode } from '@ddt-web/error-handler';

import { DataConversionUtil } from '../../utils';
import { PasAsset } from '../objects';
import { IDENTIFIER_TYPE } from '../requests';
import { AssetResolveResponse } from './asset-resolve-response.model';
import { MultiResolveSecurityDetailsResponse } from './multi-resolve-security-details-response.model';

/*	
	- Map raw PAS response to Asset Resolve Response, where requestId "IVVIX@465899813@US4658998137
	- Mapping error details from PAS Asset Resolve to errorCodes that was consumed via WWE
	  This reduces the refactoring required in existing error handlers. 
	  This is a common place to change the structure in case in future we have pas/awa changes
*/
export const mapAssetResolveResponse = (response: V1ResolveAssetsResponse): AssetResolveResponse => {
	const assetResolveResponse: AssetResolveResponse = { assets: new Map() };
	(response?.assets ?? []).forEach((assetResponse: V1AssetResponse) => {
		const asset = assetResponse.asset;
		if (asset) {
			if (!asset.ticker) {
				asset.ticker = asset.unambiguousIdentifiers.find(id => id.identifierType === IDENTIFIER_TYPE.morningstarSecId)?.identifierValue;
			}
			const esgMetrics = DataConversionUtil.convertArrayToObjectMapping(asset, 'name', 'esgMetrics');
			const isBlkFund = asset?.customClientProperties?.isBlkFund === 'true';
			const portfolioId = isBlkFund ? asset?.cusip || asset?.isin || asset?.ticker : null;
			const monthlyReturnDate = asset?.sourceDates?.returnY1AsOf || asset?.sourceDates?.returnSinceInceptionAsOf;
			assetResolveResponse.assets.set(assetResponse.requestId as string, { ...asset, portfolioId, esgMetrics, monthlyReturnDate } as PasAsset);
		}
	});

	// Map error details from PAS to error code map
	if (response.status?.details?.length) { // response?.status?.code is coming as 0 for error responses as well, hence relying on details array
		assetResolveResponse.errorCodes = new Map<string, PasErrorCode>();
		(response.status.details).forEach(row => {
			const errorDetails: PasErrorCode = {};
			errorDetails.code = response.status?.code;
			errorDetails.message = response.status?.message;
			errorDetails.requestId = row?.['requestId'];
			errorDetails.identifiers = row?.['identifiers'];
			assetResolveResponse.errorCodes.set(errorDetails?.['requestId'], errorDetails);
		});
	}

	return assetResolveResponse;
};

export const mapMultiResolveSecurityDetailsResponseBodyToMultiResolveSecurityDetailsResponse = (response: V1ResolveAmbiguousAssetsResponse): MultiResolveSecurityDetailsResponse => {
	const securityDetailsResponse: MultiResolveSecurityDetailsResponse | any = { securities: new Map() };
	(response?.resolvedAssets ?? []).forEach((securityResponseResult: V1AssetsResponse) => {
		if (securityResponseResult.assets) {
			securityDetailsResponse.securities.set(
				securityResponseResult.requestId as string,
				securityResponseResult.assets.map(security => { return { ...security, currency: security.currencyCode }; })
			);
		}
	});
	// TODO wdsDecomm: error handling
	return securityDetailsResponse;
};
